import { CoverImage, Image, SvgIcon, TextSize, Weight } from 'components'
import { Fonts, Text } from '../../../typography/text/index'
import styled, { css } from 'styled-components'

import { GLOBAL_COLORS } from 'theme'

/**
 * Wrapper around the Logo items in the header
 */
export const LogoSection = styled.a`
  cursor: pointer;
  width: 108px;
  display: flex;
  align-self: center;
  border-right: 1px solid ${GLOBAL_COLORS.secondGrey};
  margin-right: 20px;
  text-decoration: none;
  color: ${({ theme }) => theme.text1};
  transition: 0.4s all;
  &:hover {
    color: ${({ theme }) => theme.seashellWhite05ToCoreGrey};
  }
`

/**
 * Wrapper around the navigation items in the header
 */
export const MenuSection = styled.div`
  flex-grow: 1;
  display: flex;
  gap: 30px;
`

/**
 * Handle structural styling for menu items (not wallet/network)
 */
export const MenuWrapper = styled.div`
  width: 60%;
  display: flex;
  height: 22px;
  align-self: center;
`

/**
 * Wrapper for wallet and network selector on desktop
 */
export const AccountCenterWrapper = styled.div`
  width: 40%;
  display: flex;
  flex-flow: row-reverse;
  gap: 10px;
`

/**
 * Styling for the Logo text
 */
export const NavigationLogoText = styled.span`
  font-weight: ${Weight.Bold};
  font-size: ${TextSize.four};
  margin-left: 5px;
  line-height: 12px;
  display: inline-flex;
  align-items: center;
`

/**
 * Styling for the logo image
 */
export const Logo = styled(CoverImage)`
  width: 28px;
  height: 14px;
`

/**
 * Styling for the menu items (overview, portfolio, etc)
 */
export const NavigationItem = styled(Text)`
  cursor: pointer;
  font-weight: ${Weight.Medium};
  text-transform: capitalize;
  font-size: ${TextSize.four};
  line-height: 20px;
`

export const MenuListContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${({ theme }) => theme.accountCenterBg};
  align-items: center;
`

export const NestedMenuOutline = styled.div<{ isActive: boolean }>`
  position: fixed;
  top: 50px;
  left: 0;
  visibility: ${({ isActive }) => (isActive ? 'visible' : 'hidden')};
  height: ${({ isActive }) => (isActive ? 'calc(100% - 50px)' : '0')};
  transition:
    visibility 0.2s,
    height 0.3s;
  z-index: 9;
  width: 100%;
  ${({ theme }) => theme.mediaWidth.Mobile`
  height: calc(100% - 60px);
  top:60px;
`}
  ${({ theme }) => theme.mediaWidth.Tablet`
  height: calc(100% - 60px);
  top:60px;
`}
`

export const NestedMenuWrapper = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.bg};
`

export const OpenMenulistContainer = styled.div`
  width: 100%;
  max-width: 850px;
  margin: 0 auto;
  top: 20px;
  min-height: 157px;
  height: auto;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  box-sizing: border-box;
`

export const NestedMenuItems = styled.div<{ isLarge: boolean }>`
  display: flex;
  row-gap: 10px;
  column-gap: 10px;
  margin-bottom: 30px;
  ${({ isLarge }) =>
    isLarge
      ? css`
          //INFO: height for one liners(portfolio & company sections)
          height: 140px;
          justify-content: space-between;
          flex-wrap: wrap;
        `
      : css`
          //INFO: height for two liners(products section)
          height: 64px;
        `}
`

export const NestedMenuItem = styled.div<{
  highlightable: boolean
  isCompany: boolean
  isPortfolio?: boolean
}>`
  display: flex;
  align-items: center;
  height: 64px;
  padding: 15px;
  ${({ highlightable, isPortfolio, isCompany }) =>
    highlightable &&
    !isPortfolio &&
    !isCompany &&
    css`
      //INFO: width for product items
      width: 49%;
    `}

  ${({ isPortfolio }) =>
    isPortfolio &&
    css`
      //INFO: width for 2 first items in portfolio section
      width: 235px;
    `}

    ${({ highlightable }) =>
    !highlightable &&
    css`
      :last-child {
        //INFO: extra padding between non-hover items in portfolio section
        margin-left: 10px;
      }
    `}

    ${({ isCompany }) =>
    isCompany &&
    css`
      //INFO: random width for items in company section
      width: 50%;
    `}
  border-radius: 10px;
  box-sizing: border-box;
  cursor: ${({ highlightable }) => (highlightable ? 'pointer' : 'auto')};
  transition: 0.4s all;
  ${({ highlightable }) =>
    highlightable &&
    css`
      &:hover {
        background-color: ${({ theme }) => theme.nestedMenuHover};
      }
      &:active {
        opacity: 0.7;
        background-color: ${({ theme }) => theme.nestedMenuHover};
      }
    `};
  ${({ theme }) => theme.mediaWidth.Mobile`
    padding: 16px 45px;
    border-radius: 0;
    width:100% !important;
    margin-left: 0 !important;
  `}
  ${({ theme }) => theme.mediaWidth.Tablet`
    padding: 16px 45px;
    border-radius: 0;
    width:100% !important;
    margin-left: 0 !important;
  `}
`

export const PortfolioLine = styled.div`
  width: 1px;
  height: 39px;
  background-color: ${GLOBAL_COLORS.separatorBg};
  display: flex;
  align-self: center;
  ${({ theme }) => theme.mediaWidth.Mobile`
    height: 1px;
    width: calc(100% - 85px);
  `}
  ${({ theme }) => theme.mediaWidth.Tablet`
    height: 1px;
    width: calc(100% - 85px);
  `}
`

export const NestedProductIcon = styled(Image)`
  height: 32px;
  width: 32px;
  margin-right: 10px;
  border-radius: 50%;
  & > path {
    stroke: ${({ theme }) => theme.seashellWhiteToDeepOceanBlue};
  }
`

export const NestedSvgIcon = styled(SvgIcon)`
  height: 32px;
  width: 32px;
  margin-right: 10px;
  & > path {
    stroke: ${({ theme }) => theme.seashellWhiteToDeepOceanBlue};
  }
`

export const NestedMenuTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

export const MenuTitle = styled(Text)`
  font-size: ${TextSize.two};
  font-weight: ${Weight.Medium};
  margin: 30px 15px;
`

export const StyledTitle = styled.div<{ isPositive?: boolean; isZero?: boolean }>`
  font-size: ${TextSize.four};
  font-weight: ${Weight.Medium};
  font-family: ${Fonts.DMSans};
  color: ${({ isZero, isPositive, theme }) =>
    isPositive === undefined || isZero
      ? theme.text1
      : isPositive
        ? GLOBAL_COLORS.canopyGreen
        : GLOBAL_COLORS.volcanoRed};
`

export const StyledSubtitle = styled(Text)`
  font-size: ${TextSize.small};
  font-weight: ${Weight.Regular};
  opacity: 0.6;
`
